import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Button,
    Typography,
    Stack,
    List,
    ListItem,
    ListItemText,
    TextField,
} from '@mui/material';
import { fetch2FAQr, verify2FA, select2FA, reset2FAState } from '../../store/twoFAlice';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';

const steps = [
    'Introduction',
    'Scan QR Code',
    'Verify Code',
];

const OtpInput = ({ value, onChange, onKeyDown, inputRef, disabled }) => {
    return (
        <TextField
            inputRef={inputRef}
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            disabled={disabled}
            color="secondary"
            inputProps={{
                maxLength: 1,
                style: { textAlign: 'center', fontSize: '1.5rem', padding: '0 0 8px 0' },
                type: 'text',
            }}
            variant="standard"
        />
    );
};

function TwoFaSetupStepper({ email, password, onSuccess, onError }) {
    const dispatch = useDispatch();

    const { qrCode, loading, verified, error } = useSelector(select2FA);
    const [activeStep, setActiveStep] = useState(0);
    const [code, setCode] = useState(Array(6).fill(''));
    const inputsRef = useRef([]);
    const [storedEmail, setStoredEmail] = useState(null);

    // Reset 2FA state on component mount and when email changes (different user logged in)
    useEffect(() => {
        if (email) {
            if (email !== storedEmail || !storedEmail) {
                // Email changed (different user) or component just mounted, reset all state and clear QR code
                dispatch(reset2FAState());
                setStoredEmail(email);
                setActiveStep(0);
                setCode(Array(6).fill(''));
            }
        }
    }, [email, storedEmail, dispatch]);

    // Fetch QR code when moving to step 1 (QR code step) - always fetch fresh for current email
    useEffect(() => {
        if (activeStep === 1 && email && email === storedEmail) {
            // Always fetch fresh QR code when on QR step for the current email
            // Reset was already called if email changed
            dispatch(fetch2FAQr({ email }));
        }
    }, [activeStep, email, storedEmail, dispatch]);

    const handleNext = () => {
        if (activeStep === 0) {
            // Move to QR code step
            setActiveStep(1);
        } else if (activeStep === 1) {
            // Move to verify step
            setActiveStep(2);
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleChange = (e, idx) => {
        const val = e.target.value.replace(/\D/g, '');
        const newCode = [...code];
        newCode[idx] = val ? val[0] : '';
        setCode(newCode);

        if (val && idx < code.length - 1) {
            inputsRef.current[idx + 1].focus();
        }
    };

    const handleKeyDown = (e, idx) => {
        if (e.key === 'Backspace' && !code[idx] && idx > 0) {
            const newCode = [...code];
            newCode[idx - 1] = '';
            setCode(newCode);
            inputsRef.current[idx - 1].focus();
        }
    };

    const handleVerify = () => {
        if (code.every(digit => digit !== '')) {
            dispatch(verify2FA({ token: code.join(''), email, password }))
                .unwrap()
                .then((res) => {
                    if (onSuccess) {
                        onSuccess(res);
                    }
                })
                .catch((_errors) => {
                    if (onError) {
                        onError(_errors);
                    }
                });
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box>
                        <Typography variant="h5" gutterBottom>
                            Set Up 2-Factor Authentication
                        </Typography>

                        <List component="ol" sx={{ listStyle: 'decimal', pl: 3 }}>
                            <ListItem sx={{ display: 'list-item', pb: 2 }}>
                                <ListItemText
                                    primary={
                                        <Typography sx={{ wordBreak: 'break-word' }}>
                                            Download or open an authenticator app:
                                        </Typography>
                                    }
                                    secondary={
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={2}
                                            sx={{ mt: 2, flexWrap: 'wrap' }}
                                        >
                                            <Button
                                                onClick={() =>
                                                    window.open(
                                                        "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2",
                                                        "_blank"
                                                    )
                                                }
                                                variant="outlined"
                                                startIcon={<AndroidIcon />}
                                                sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' }, width: { xs: '100%', sm: 'auto' } }}
                                            >
                                                Google Play
                                            </Button>

                                            <Button
                                                onClick={() =>
                                                    window.open(
                                                        "https://apps.apple.com/us/app/google-authenticator/id388497605",
                                                        "_blank"
                                                    )
                                                }
                                                variant="outlined"
                                                startIcon={<AppleIcon />}
                                                sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' }, width: { xs: '100%', sm: 'auto' } }}
                                            >
                                                Apple Store
                                            </Button>

                                            <Button
                                                onClick={() =>
                                                    window.open("https://support.apple.com/en-us/102654", "_blank")
                                                }
                                                variant="outlined"
                                                startIcon={<AppleIcon />}
                                                sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' }, width: { xs: '100%', sm: 'auto' } }}
                                            >
                                                Apple Password
                                            </Button>
                                        </Stack>
                                    }
                                />
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', pb: 2 }}>
                                <ListItemText primary="Add a new device using the '+' button in the bottom right corner." />
                            </ListItem>

                            <ListItem sx={{ display: 'list-item' }}>
                                <ListItemText primary="Select 'Scan a QR Code' or 'Enter a setup key'. The next screen will show you the QR code and key." />
                            </ListItem>
                        </List>
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Scan the QR Code
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Scan the QR code below using the Authenticator app on your mobile device.
                        </Typography>
                        {loading && (
                            <Typography sx={{ mt: 3 }}>Loading QR Code...</Typography>
                        )}
                        {!loading && qrCode && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 3 }}>
                                <img
                                    src={qrCode}
                                    alt="Authenticator QR"
                                    style={{ borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                                />
                            </Box>
                        )}
                        {error && (
                            <Typography color="error" sx={{ mt: 2 }}>
                                {error}
                            </Typography>
                        )}
                    </Box>
                );

            case 2:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Verify Authentication
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Enter the 6-digit code generated by your Authenticator app.
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', maxWidth: '320px', gap: 2, mx: 'auto', mt: 3 }}>
                            {code.map((digit, idx) => (
                                <OtpInput
                                    key={idx}
                                    value={digit}
                                    onChange={e => handleChange(e, idx)}
                                    onKeyDown={e => handleKeyDown(e, idx)}
                                    inputRef={el => (inputsRef.current[idx] = el)}
                                    disabled={loading}
                                />
                            ))}
                        </Box>
                        {error && (
                            <Typography color="error" sx={{ mt: 2 }}>
                                {error}
                            </Typography>
                        )}
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Box sx={{ width: '100%', mt: 4 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((label, index) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                        <StepContent>
                            {renderStepContent(index)}
                            <Box sx={{ mb: 2, mt: 3 }}>
                                <div>
                                    <Button
                                        variant="contained"
                                        color='secondary'
                                        onClick={index === 2 ? handleVerify : handleNext}
                                        disabled={
                                            (index === 1 && (!qrCode || loading)) ||
                                            (index === 2 && (!code.every(d => d !== '') || loading))
                                        }
                                        sx={{ mt: 1, mr: 1 }}
                                    >
                                        {index === 2 ? (loading ? 'Verifying...' : 'Verify') : 'Continue'}
                                    </Button>
                                    {index > 0 && (
                                        <Button
                                            onClick={handleBack}
                                            sx={{ mt: 1, mr: 1 }}
                                        >
                                            Back
                                        </Button>
                                    )}
                                </div>
                            </Box>
                        </StepContent>
                    </Step>
                ))}
            </Stepper>
        </Box>
    );
}

export default TwoFaSetupStepper;
